import React, { useState } from 'react'
import { cn } from '../../lib/utils'
import Moment from 'moment'
import { useTranslation } from '../../hooks/useTranslation'
import { Button, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import ReminderDialog, { useReminderDialog } from './ReminderDialog'
import {
  PencilSquareIcon,
  CheckBadgeIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { apiPatch } from '../../util/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/ui/dialog'
import { FlagIcon } from './Icons'
import { useFormatTime } from '~/hooks/useFormatTime'
import safeParse from '~/lib/safeParse'

export const InjectEventStartTime = (str, date, formatTime) => {
  if (!str) return str
  if (str.includes('%{eventStartTime}')) {
    return str.replace(
      '%{eventStartTime}',
      formatTime(date, 'meeting_datetime'),
    )
  } else
    return str.replace(
      '${eventStartTime}',
      formatTime(date, 'meeting_datetime'),
    )
}

const PromoteSkillDialog = ({
  open,
  onOpenChange,
  handleCompleteReminder,
  skillName,
  children,
}) => {
  const { t } = useTranslation('promote_skill_dialog')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild className="cursor-pointer">
        {children}
      </DialogTrigger>
      <DialogContent
        className="site-info-dialog roboto max-w-xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="font-roboto-slab text-xl tracking-[0.12px]">
            {t('promote_skill')}
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          <p>{t('add_skill_to_inventory', { skillName })}</p>
          <div className="flex flex-row justify-between pt-4">
            <Button onClick={handleCompleteReminder}>{t('complete')}</Button>
            <Button
              onClick={() => handleCompleteReminder({ promoteSkill: true })}
            >
              {t('complete_and_add')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const Reminders = ({ className }) => {
  const { reminders, toggleShowAddReminderDialog } = useReminderDialog()
  const { t } = useTranslation('reminders')

  return (
    <Stack className={className} spacing={3}>
      <Typography>{t('description')}</Typography>
      <ReminderDialog>
        <Button
          component="button"
          variant="outline"
          startIcon={<PlusIcon />}
          onClick={toggleShowAddReminderDialog}
        >
          {t('new_action_text')}
        </Button>
      </ReminderDialog>
      {reminders.map((reminder, idx) => (
        <ReminderTile
          key={`reminders-${idx}`}
          reminder={reminder}
        ></ReminderTile>
      ))}
      <div>
        <a href="/reminders">{t('see_all_reminders')}</a>
      </div>
    </Stack>
  )
}
export default Reminders

const ReminderTile = ({ reminder }) => {
  const { t } = useTranslation('reminder_tile')
  const { t: tc } = useTranslation('')

  const { setReminders, setEditingReminder, setShowAddReminderDialog, locale } =
    useReminderDialog()
  const [reminderCompleted, setReminderCompleted] = useState(false)
  const [showPromoteSkillDialog, setShowPromoteSkillDialog] = useState(false)
  const [border, setBorder] = useState('border')
  const [transitionDuration, setTransitionDuration] = useState('duration-2000')
  const [divHeight, setDivHeight] = useState('max-h-full min-h-48')
  const reminderAnimation = reminderCompleted
    ? 'animate__animated animate__fadeOut'
    : ''
  const background = reminderCompleted
    ? 'bg-intoo-blue-medium bg-opacity-95'
    : 'bg-white'

  const {
    content,
    due_date: dueDate,
    linked_record_path: linkedRecordPath,
    linked_record_text: linkedRecordText,
    webinar_link: webinarLink,
    saved_recruiter_id: savedRecruiterId,
    skill_name: skillName,
    document_id: documentId,
    event_start_time: eventStartTime,
  } = reminder

  const mayPromoteSkill = !!(skillName && documentId)
  const isPastDue = Moment(dueDate).isSameOrBefore(Moment(), 'day')

  const togglePromoteSkillDialog = () => {
    setShowPromoteSkillDialog(!showPromoteSkillDialog)
  }

  const removeReminderFromList = (completedReminder) => {
    setReminders((prev) => prev.filter((r) => r.id !== completedReminder.id))
  }

  const completedReminderAnimation = (completedReminder) => {
    setReminderCompleted(true)
    setTimeout(() => {
      setBorder('')
      setDivHeight('h-0 min-h-0')
      setTransitionDuration('duration-200')
    }, 2000)
    setTimeout(() => {
      removeReminderFromList(completedReminder)
      setReminderCompleted(false)
      setDivHeight('max-h-full min-h-48')
      setTransitionDuration('')
      setBorder('border')
    }, 2200)
    setTimeout(() => {
      setTransitionDuration('duration-2000')
    }, 2550)
  }

  const handleCompleteReminder = async ({ promoteSkill = false }) => {
    const { id } = reminder
    const data = { reminder: { promote_skill: promoteSkill } }

    setShowPromoteSkillDialog(false)

    const res = await apiPatch(
      `/reminders/${id}/complete?page=1&per_page=5`,
      data,
    )
    if (res.status === 200) {
      if (res.data && res.data.reminders) {
        completedReminderAnimation(reminder)
        setTimeout(() => {
          setReminders(res.data.reminders)
        }, 2550)
      }
      return res
    } else {
      console.log(res)
      console.log(res.status)
      console.log(res.error)
      throw new Error('res not ok')
    }
  }

  const handleEditReminder = () => {
    setEditingReminder(reminder)
    setShowAddReminderDialog(true)
  }

  const convertFromNow = () => {
    return Moment(dueDate).calendar(null, {
      sameDay: '[Today]',
      nextDay: '[Tomorrow]',
    })
  }

  // Translate 'Today' and 'Tomorrow' for display
  const translateDueLabel = (label) => {
    if (label === 'Today') return tc('today')
    if (label === 'Tomorrow') return tc('tomorrow')
    return label
  }

  const formatTime = useFormatTime(locale)

  const todayOrTomorrow = convertFromNow()
  const momentDueDate = dueDate ? Moment(dueDate).locale(locale) : null

  const due = dueDate
    ? ['Today', 'Tomorrow'].includes(todayOrTomorrow)
      ? todayOrTomorrow
      : momentDueDate.format('L')
    : t('not_set')

  const borderColor = isPastDue
    ? 'border-[#E10000]'
    : ['Tomorrow'].includes(due)
      ? 'border-[#FF990059]'
      : 'border-[#D6D6D6]'

  const isRecruiterFollowUp =
    savedRecruiterId && content.startsWith('Follow up with ')
  const recruiterName = isRecruiterFollowUp
    ? // Extract the recruiter name from the content
      content.slice('Follow up with '.length)
    : null

  return (
    <div
      id={`reminder-${reminder.id}`}
      className={cn(
        'flex-flex-col overflow-hidden rounded-[20px] border-solid bg-white shadow-dashboard-reminder-tile',
        borderColor,
        reminderAnimation,
        background,
        transitionDuration,
        divHeight,
        border,
      )}
    >
      <>
        {reminderCompleted && (
          <div className="relative top-12 flex w-full justify-center">
            <CheckBadgeIcon className="h-20 text-white" />
          </div>
        )}
        {!reminderCompleted && (
          <>
            <div className="flex items-center justify-between p-4">
              <div className={isPastDue ? 'flex text-[#E10000]' : ''}>
                {t('due_date')}: {translateDueLabel(due)}{' '}
                {isPastDue && <FlagIcon className="ml-2" />}
              </div>
              {/* CTS-9235: keyboard-accessible edit control */}
              <button
                type="button"
                onClick={handleEditReminder}
                aria-label={`${t('edit_reminder')}: ${content}`}
                className="cursor-pointer rounded p-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-intoo-blue-medium-700 focus-visible:ring-offset-2"
              >
                <PencilSquareIcon className="h-6" aria-hidden="true" />
              </button>
            </div>
            <hr />
            <div className="relative flex min-h-32 flex-col justify-between gap-2">
              <div className="p-4">
                {isRecruiterFollowUp ? (
                  <>
                    {t('follow_up_with')}{' '}
                    <a
                      href={`/job-search/recruiters/my_recruiters?expand=${savedRecruiterId}`}
                      className="!underline"
                    >
                      {recruiterName}
                    </a>
                  </>
                ) : (
                  <>
                    {content &&
                      safeParse(
                        InjectEventStartTime(
                          content,
                          eventStartTime,
                          formatTime,
                        ),
                      )}{' '}
                    {webinarLink && (
                      <LinkifiedCopy copy={t('join_now')} link={webinarLink} />
                    )}
                    {linkedRecordPath && (
                      <a href={linkedRecordPath} className="!underline">
                        {linkedRecordText}
                      </a>
                    )}
                  </>
                )}
              </div>
              <div className="self-end p-4">
                {mayPromoteSkill ? (
                  <PromoteSkillDialog
                    open={showPromoteSkillDialog}
                    onOpenChange={togglePromoteSkillDialog}
                    handleCompleteReminder={handleCompleteReminder}
                    skillName={skillName}
                  >
                    <Button
                      component="button"
                      onClick={togglePromoteSkillDialog}
                    >
                      {t('complete')}
                    </Button>
                  </PromoteSkillDialog>
                ) : (
                  <Button component="button" onClick={handleCompleteReminder}>
                    {t('complete')}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </>
    </div>
  )
}

const LinkifiedCopy = ({ copy, link }) => {
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="!underline"
    >
      {copy}
    </a>
  )
}
